import {
  CASE_SORT,
  CASE_SORT_PARAM,
  type CaseSortKey,
  type SortableCase,
  type SortPreset,
} from '@/domain';
import { strings } from '@/shared/strings';

/**
 * The floor's names for the orders a case table can be read in, paired with the
 * domain's comparators. Section 3 of the request of 13 September 2026.
 *
 * Here rather than in each screen so that Urgent first means the same thing on
 * Orders, at a bench and at Final Check, and so that renaming one renames it
 * everywhere. Which presets a table offers is still the screen's business: a
 * list of orders has no bench time to wait at, and a list of deleted cases has
 * no due date left to be due on.
 */
export const CASE_SORT_LABEL: Record<CaseSortKey, string> = {
  dueSoonest: strings.table.dueSoonest,
  urgentFirst: strings.table.urgentFirst,
  newestFirst: strings.table.newestFirst,
  oldestFirst: strings.table.oldestFirst,
  longestWaiting: strings.table.longestWaiting,
  doctorAtoZ: strings.table.doctorAtoZ,
  localFirst: strings.table.localFirst,
  internationalFirst: strings.table.internationalFirst,
  onlineFirst: strings.table.onlineFirst,
};

/** The three origin sorts, in the client's order. Section 2 of the request of 24 September 2026. */
export const ORIGIN_SORT_KEYS = ['localFirst', 'internationalFirst', 'onlineFirst'] as const;

/**
 * The presets a screen offers, in the order they are offered. The FIRST is the
 * default, and since section 2 of the request of 5 October 2026 every list's
 * first is newest first. A reader who chose another order keeps it: the choice
 * is remembered per list.
 */
export function casePresets<Row extends SortableCase>(
  keys: readonly CaseSortKey[],
): SortPreset<Row>[] {
  return keys.map((key) => {
    // The origin sorts also go to the server, for a list that is paged. The rows
    // come back in that order and the comparator keeps them in it.
    const sortParam = CASE_SORT_PARAM[key];
    return {
      key,
      label: CASE_SORT_LABEL[key],
      compare: CASE_SORT[key],
      ...(sortParam === undefined ? {} : { sortParam }),
    };
  });
}
